import React, { useMemo, useState, useEffect } from 'react';
import { MantineReactTable } from 'mantine-react-table';
import { useTheme } from '../../../ThemeContext'; // Adjust the path as necessary
import { MantineProvider, Modal, Button, Loader, Text } from '@mantine/core';
import { formatDate } from '../../../api/services';
import VibrationSimulationForm from '../Forms/Reports'; // Assuming the form component is imported
import { getAllQualityInspection, deleteQualityInspection } from '../../../api/api';
import { EyeIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../ConfirmationModal';
const VibrationSimulation = () => {
  const { theme } = useTheme(); // Get the theme from context
  const [data, setData] = useState([]);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false); // Loader state
  const [update, setUpdate] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // Manage modal visibility
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const handleOpen = () => setIsOpen(true); // Open modal
  const handleClose = () => setIsOpen(false); // Close modal

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Set loading to true when data fetch starts
      try {
        const res = await getAllQualityInspection();
        setData(res.data.qualityinspections); // Set data from response
        // console.log(res.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false); // Set loading to false after data is fetched
    };
    fetchData();
  }, [update]);

  const columns = useMemo(() => [
    {
      accessorKey: 'contractorName.name',
      header: 'Contractor Name',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'inspectionRequest',
      header: 'Inspection Request ',
    },

    {
      accessorKey: 'checklistFile',
      header: 'Checklist File',
      Cell: ({ cell }) => cell.getValue() || 'N/A',
    },
    {
      accessorKey: 'submissionDate',
      header: 'Submission Date',
      Cell: ({ cell }) => formatDate(cell.getValue())
    },
    {
      accessorKey: 'status',
      header: 'Status',
    },
    {
      accessorKey: 'checkList',
      header: 'Checklist',
      Cell: ({ cell }) => {
        try {
          // Parse the JSON string into an array
          const checklistItems = JSON.parse(cell.getValue());
          // Return the items as a comma-separated list or individual elements
          return (
            <ul className="list-disc pl-5 text-blue-500">
              {checklistItems.map((item, index) => (
                <li key={index} className="my-1">
                  {item}
                </li>
              ))}
            </ul>
          );
        } catch (error) {
          console.error('Error parsing checkList:', error);
          return 'Invalid Data';
        }
      },
    },
    {
      accessorKey: 'comment',
      header: 'Comments',
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <div className="flex space-x-2">
          <PencilIcon
            className="w-5 h-5 text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => handleEditRow(row)}
          />
          <TrashIcon
            className="w-5 h-5 text-red-600 hover:text-red-800 cursor-pointer"
            onClick={() => openDeleteConfirmation(row)}
          />
        </div>
      ),
    },
  ], []);

  const handleUpdate = () => {
    setUpdate(!update)
  }
  const openDeleteConfirmation = (row) => {

    setEdit(true)
    setConfirmModalOpen(true);
    setEditData(row.original);
  };

  const handleDeleteRow = async () => {
    //  console.log(editData._id);
    if (editData) {
      try {
        await deleteQualityInspection(editData._id);
        setConfirmModalOpen(false);
        handleUpdate();
        setEdit(false)
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleEditRow = (row) => {
  
    setEditData(row.original);

    setIsOpen(true);
  };

  return (
    <MantineProvider
      theme={{
        colorScheme: theme,
        primaryColor: 'blue',
        primaryShade: 5,
      }}
    >
      {/* Header Section */}
      <div className="flex justify-between p-2 bg-gray-700 items-center text-md">
        <Text className='text-lg font-semibold'>Quality Inspection</Text>
        <Button
          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
          onClick={handleOpen}
        >
          Add Data
        </Button>
      </div>

      {/* Loader while fetching data */}
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <Loader type="ThreeDots" color="#00BFFF" />
        </div>
      ) : (
        <>
          {/* Quality Inspection Table */}
          <div className="overflow-x-auto max-h-screen">
            <MantineReactTable
              columns={columns}
              data={data}
              enableColumnActions
              enableColumnFilters
              enablePagination
              enableSorting
              enableRowNumbers
              enableGlobalFilter
              mantineTableProps={{
                highlightOnHover: true,
                withBorder: true,
                sx: {
                  'thead > tr': {
                    backgroundColor: theme === 'dark' ? '#1a202c' : '#f7fafc',
                  },
                  'thead > tr > th': {
                    backgroundColor: theme === 'dark' ? '#2d3748' : '#edf2f7',
                    color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                  },
                  'tbody > tr': {
                    '&:hover': {
                      backgroundColor: theme === 'dark' ? '#2d3748' : '#f1f5f9',
                    },
                  },
                  'tbody > tr > td': {
                    backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
                    color: theme === 'dark' ? '#ffff' : '#333333',
                  },
                },
              }}
            />
          </div>

          {/* Modal for Vibration Simulation Form */}
          {isOpen && (
            <VibrationSimulationForm onClose={handleClose} isOpen={isOpen} edit={edit} setEdit={setEdit} editData={editData} handleUpdate={handleUpdate} />
          )}
          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModalOpen}
            onClose={() => setConfirmModalOpen(false)}
            onConfirm={handleDeleteRow}
            title="Confirm Deletion"
            message="Are you sure you want to delete this?"
          />
        </>
      )}
    </MantineProvider>
  );
};

export default VibrationSimulation;
